import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  User,
  Target,
  Star,
  TrendingUp,
  Eye,
  MessageSquare,
  Award,
  BookOpen,
  Users,
  Lightbulb,
  CheckCircle,
  AlertCircle,
  ArrowRight,
  Sparkles,
  Brain,
  Heart,
  Zap,
  PieChart,
  BarChart3,
  Activity
} from 'lucide-react';
import { Card } from '../shared/Card';
import { Button } from '../shared/Button';
import { Badge } from '../shared/Badge';
import { ProgressBar } from '../shared/ProgressBar';

interface BrandAttribute {
  name: string;
  score: number;
  description: string;
  examples: string[];
  improvementTips: string[];
}

interface BrandAnalysis {
  overallScore: number;
  brandStrength: 'Exceptional' | 'Strong' | 'Developing' | 'Needs Work';
  primaryPersona: string;
  secondaryPersona: string;
  uniqueValueProposition: string;
  attributes: BrandAttribute[];
  strengths: string[];
  growthAreas: string[];
  competitiveAdvantages: string[];
  recommendations: {
    immediate: string[];
    shortTerm: string[];
    longTerm: string[];
  };
  brandConsistency: number;
  narrativeCoherence: number;
  differentiationFactor: number;
}

const sampleAnalysis: BrandAnalysis = {
  overallScore: 78,
  brandStrength: 'Strong',
  primaryPersona: 'The Compassionate Innovator',
  secondaryPersona: 'The Research-Driven Problem Solver',
  uniqueValueProposition: 'A STEM-focused student who combines technical excellence with deep empathy for social impact, uniquely positioned to bridge technology and human needs.',
  attributes: [
    {
      name: 'Academic Excellence',
      score: 85,
      description: 'Demonstrates consistent high performance across challenging coursework',
      examples: ['4.2 GPA in honors courses', 'AP Scholar Award', 'Math competition finalist'],
      improvementTips: ['Consider taking additional challenging courses', 'Pursue academic research opportunities']
    },
    {
      name: 'Leadership Impact',
      score: 72,
      description: 'Shows growing leadership skills with measurable community impact',
      examples: ['Science club president', 'Organized school recycling program', 'Tutored 15+ students'],
      improvementTips: ['Scale leadership initiatives', 'Document quantifiable outcomes', 'Seek regional leadership roles']
    },
    {
      name: 'Innovation & Creativity',
      score: 80,
      description: 'Demonstrates creative problem-solving and innovative thinking',
      examples: ['Created educational app', 'Science fair regional winner', 'Invented recycling solution'],
      improvementTips: ['Apply for innovation competitions', 'Document the creative process', 'Seek patents or publications']
    },
    {
      name: 'Social Responsibility',
      score: 88,
      description: 'Strong commitment to community service and social impact',
      examples: ['150+ volunteer hours', 'Environmental club founder', 'Community garden project leader'],
      improvementTips: ['Connect service to career goals', 'Measure impact more systematically']
    },
    {
      name: 'Communication Skills',
      score: 65,
      description: 'Developing strong communication abilities across various mediums',
      examples: ['Student newspaper contributor', 'Presentation awards', 'Peer tutoring feedback'],
      improvementTips: ['Join debate team or Model UN', 'Start a blog or podcast', 'Seek public speaking opportunities']
    },
    {
      name: 'Global Perspective',
      score: 58,
      description: 'Growing awareness of global issues and cultural diversity',
      examples: ['International pen pal program', 'Cultural exchange participant', 'Language study'],
      improvementTips: ['Pursue international experiences', 'Learn additional languages', 'Engage with global issues']
    }
  ],
  strengths: [
    'Exceptional academic performance with consistent excellence',
    'Strong technical skills combined with social consciousness',
    'Natural leadership qualities with measurable community impact',
    'Creative problem-solving approach to real-world challenges',
    'Authentic passion for environmental sustainability'
  ],
  growthAreas: [
    'Expand communication and public speaking experience',
    'Develop stronger global and cultural awareness',
    'Scale leadership initiatives to regional or national level',
    'Build more systematic approach to measuring impact',
    'Strengthen personal narrative coherence across activities'
  ],
  competitiveAdvantages: [
    'Unique combination of STEM excellence and social impact focus',
    'Proven ability to translate ideas into actionable solutions',
    'Strong track record of community engagement and leadership',
    'Authentic passion that drives consistent high performance'
  ],
  recommendations: {
    immediate: [
      'Document all achievements with specific metrics and outcomes',
      'Create a cohesive portfolio showcasing the innovation-impact connection',
      'Seek mentorship from professionals in sustainability or social entrepreneurship'
    ],
    shortTerm: [
      'Apply for summer programs combining STEM and social impact',
      'Start a blog or social media presence around sustainability innovations',
      'Pursue speaking opportunities at conferences or community events'
    ],
    longTerm: [
      'Develop a signature project that embodies your unique value proposition',
      'Build relationships with university professors in your areas of interest',
      'Consider launching a social enterprise or research initiative'
    ]
  },
  brandConsistency: 82,
  narrativeCoherence: 75,
  differentiationFactor: 84
};

export const PersonalBrandAnalyzer: React.FC = () => {
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analysisComplete, setAnalysisComplete] = useState(false);
  const [selectedView, setSelectedView] = useState<'overview' | 'attributes' | 'recommendations'>('overview');
  const [progress, setProgress] = useState(0);

  const runAnalysis = async () => {
    setIsAnalyzing(true);
    setProgress(0);

    const steps = [
      { message: 'Analyzing academic achievements...', progress: 20 },
      { message: 'Evaluating leadership experiences...', progress: 40 },
      { message: 'Assessing creative projects and innovations...', progress: 60 },
      { message: 'Measuring community impact and service...', progress: 80 },
      { message: 'Generating personal brand insights...', progress: 100 }
    ];

    for (const step of steps) {
      await new Promise(resolve => setTimeout(resolve, 1500));
      setProgress(step.progress);
    }

    setIsAnalyzing(false);
    setAnalysisComplete(true);
  };

  const getScoreColor = (score: number) => {
    if (score >= 80) return 'green';
    if (score >= 70) return 'blue';
    if (score >= 60) return 'yellow';
    return 'red';
  };

  const getBrandStrengthColor = (strength: string) => {
    switch (strength) {
      case 'Exceptional': return 'text-green-600 bg-green-100';
      case 'Strong': return 'text-blue-600 bg-blue-100';
      case 'Developing': return 'text-yellow-600 bg-yellow-100';
      default: return 'text-red-600 bg-red-100';
    }
  };

  return (
    <div className="space-y-8">
      {/* Header */}
      <Card className="bg-gradient-to-br from-primary-500 to-accent-500 text-white">
        <div className="text-center">
          <User className="w-12 h-12 mx-auto mb-4" />
          <h2 className="text-3xl font-bold mb-4">AI Personal Brand Analyzer</h2>
          <p className="text-primary-100 text-lg mb-6">
            Discover your unique story and build a compelling personal brand that stands out to admissions officers
          </p>
          {!analysisComplete && (
            <Button
              onClick={runAnalysis}
              disabled={isAnalyzing}
              variant="secondary"
              className="bg-white text-primary-600 hover:bg-gray-50"
            >
              <Brain size={20} className="mr-2" />
              {isAnalyzing ? 'Analyzing Your Brand...' : 'Analyze My Personal Brand'}
            </Button>
          )}
        </div>
      </Card>

      {/* Analysis Progress */}
      {isAnalyzing && (
        <Card>
          <div className="text-center">
            <motion.div
              animate={{ rotate: 360 }}
              transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
              className="w-12 h-12 border-4 border-primary-500 border-t-transparent rounded-full mx-auto mb-4"
            />
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              AI Analyzing Your Personal Brand...
            </h3>
            <ProgressBar progress={progress} color="primary" showPercentage className="mb-4" />
            <p className="text-gray-600">
              Our AI is evaluating your activities, achievements, and unique qualities to identify your personal brand
            </p>
          </div>
        </Card>
      )}

      {/* Analysis Results */}
      <AnimatePresence>
        {analysisComplete && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-6"
          >
            {/* Brand Overview */}
            <Card>
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-semibold text-gray-900">Your Personal Brand Analysis</h3>
                <Badge variant="success">
                  <CheckCircle size={14} className="mr-1" />
                  Analysis Complete
                </Badge>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
                <div className="text-center">
                  <div className="text-4xl font-bold text-primary-600 mb-2">
                    {sampleAnalysis.overallScore}
                  </div>
                  <div className="text-sm text-gray-600 mb-2">Overall Brand Score</div>
                  <Badge className={getBrandStrengthColor(sampleAnalysis.brandStrength)}>
                    {sampleAnalysis.brandStrength}
                  </Badge>
                </div>

                <div className="text-center">
                  <div className="text-4xl font-bold text-secondary-600 mb-2">
                    {sampleAnalysis.brandConsistency}%
                  </div>
                  <div className="text-sm text-gray-600">Brand Consistency</div>
                </div>

                <div className="text-center">
                  <div className="text-4xl font-bold text-accent-600 mb-2">
                    {sampleAnalysis.differentiationFactor}%
                  </div>
                  <div className="text-sm text-gray-600">Differentiation Factor</div>
                </div>
              </div>

              <div className="bg-gradient-to-r from-primary-50 to-accent-50 rounded-xl p-6">
                <h4 className="font-semibold text-gray-800 mb-3">Your Brand Persona</h4>
                <div className="mb-4">
                  <span className="text-lg font-semibold text-primary-700">
                    {sampleAnalysis.primaryPersona}
                  </span>
                  <span className="text-gray-600 mx-2">•</span>
                  <span className="text-secondary-700">
                    {sampleAnalysis.secondaryPersona}
                  </span>
                </div>
                <p className="text-gray-700 leading-relaxed">
                  {sampleAnalysis.uniqueValueProposition}
                </p>
              </div>
            </Card>

            {/* Navigation Tabs */}
            <Card>
              <div className="flex border-b border-gray-200 mb-6">
                {[
                  { id: 'overview', label: 'Overview', icon: Eye },
                  { id: 'attributes', label: 'Brand Attributes', icon: BarChart3 },
                  { id: 'recommendations', label: 'Recommendations', icon: Lightbulb }
                ].map(({ id, label, icon: Icon }) => (
                  <button
                    key={id}
                    onClick={() => setSelectedView(id as any)}
                    className={`flex items-center space-x-2 px-4 py-3 font-medium text-sm border-b-2 transition-colors ${
                      selectedView === id
                        ? 'border-primary-500 text-primary-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    <Icon size={16} />
                    <span>{label}</span>
                  </button>
                ))}
              </div>

              <AnimatePresence mode="wait">
                {selectedView === 'overview' && (
                  <motion.div
                    key="overview"
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: 20 }}
                    className="space-y-6"
                  >
                    {/* Strengths */}
                    <div>
                      <h4 className="font-semibold text-gray-800 mb-4 flex items-center">
                        <Star className="text-yellow-500 mr-2" size={18} />
                        Key Strengths
                      </h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {sampleAnalysis.strengths.map((strength, index) => (
                          <div key={index} className="flex items-start space-x-3 p-3 bg-green-50 rounded-lg">
                            <CheckCircle size={16} className="text-green-600 mt-0.5 flex-shrink-0" />
                            <span className="text-sm text-gray-700">{strength}</span>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Growth Areas */}
                    <div>
                      <h4 className="font-semibold text-gray-800 mb-4 flex items-center">
                        <TrendingUp className="text-blue-500 mr-2" size={18} />
                        Growth Opportunities
                      </h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {sampleAnalysis.growthAreas.map((area, index) => (
                          <div key={index} className="flex items-start space-x-3 p-3 bg-blue-50 rounded-lg">
                            <ArrowRight size={16} className="text-blue-600 mt-0.5 flex-shrink-0" />
                            <span className="text-sm text-gray-700">{area}</span>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Competitive Advantages */}
                    <div>
                      <h4 className="font-semibold text-gray-800 mb-4 flex items-center">
                        <Award className="text-purple-500 mr-2" size={18} />
                        Competitive Advantages
                      </h4>
                      <div className="space-y-2">
                        {sampleAnalysis.competitiveAdvantages.map((advantage, index) => (
                          <div key={index} className="flex items-start space-x-3 p-3 bg-purple-50 rounded-lg">
                            <Sparkles size={16} className="text-purple-600 mt-0.5 flex-shrink-0" />
                            <span className="text-sm text-gray-700">{advantage}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </motion.div>
                )}

                {selectedView === 'attributes' && (
                  <motion.div
                    key="attributes"
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: 20 }}
                    className="space-y-6"
                  >
                    {sampleAnalysis.attributes.map((attribute, index) => (
                      <div key={attribute.name} className="border border-gray-200 rounded-xl p-6">
                        <div className="flex items-center justify-between mb-4">
                          <h4 className="text-lg font-semibold text-gray-900">{attribute.name}</h4>
                          <div className="flex items-center space-x-3">
                            <ProgressBar
                              progress={attribute.score}
                              color={getScoreColor(attribute.score) as any}
                              className="w-24"
                              size="sm"
                            />
                            <span className={`text-lg font-bold ${
                              attribute.score >= 80 ? 'text-green-600' :
                              attribute.score >= 70 ? 'text-blue-600' :
                              attribute.score >= 60 ? 'text-yellow-600' : 'text-red-600'
                            }`}>
                              {attribute.score}
                            </span>
                          </div>
                        </div>

                        <p className="text-gray-700 mb-4">{attribute.description}</p>

                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                          <div>
                            <h5 className="font-medium text-gray-800 mb-2">Evidence Examples:</h5>
                            <ul className="space-y-1">
                              {attribute.examples.map((example, idx) => (
                                <li key={idx} className="text-sm text-gray-600 flex items-center space-x-2">
                                  <div className="w-1.5 h-1.5 bg-green-400 rounded-full flex-shrink-0" />
                                  <span>{example}</span>
                                </li>
                              ))}
                            </ul>
                          </div>

                          <div>
                            <h5 className="font-medium text-gray-800 mb-2">Improvement Tips:</h5>
                            <ul className="space-y-1">
                              {attribute.improvementTips.map((tip, idx) => (
                                <li key={idx} className="text-sm text-gray-600 flex items-center space-x-2">
                                  <div className="w-1.5 h-1.5 bg-blue-400 rounded-full flex-shrink-0" />
                                  <span>{tip}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        </div>
                      </div>
                    ))}
                  </motion.div>
                )}

                {selectedView === 'recommendations' && (
                  <motion.div
                    key="recommendations"
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: 20 }}
                    className="space-y-6"
                  >
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                      {/* Immediate Actions */}
                      <div className="bg-red-50 rounded-xl p-6">
                        <h4 className="font-semibold text-red-800 mb-4 flex items-center">
                          <Zap className="mr-2" size={18} />
                          Immediate Actions
                        </h4>
                        <div className="space-y-3">
                          {sampleAnalysis.recommendations.immediate.map((action, index) => (
                            <div key={index} className="flex items-start space-x-2">
                              <AlertCircle size={14} className="text-red-600 mt-1 flex-shrink-0" />
                              <span className="text-sm text-gray-700">{action}</span>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Short-term Goals */}
                      <div className="bg-yellow-50 rounded-xl p-6">
                        <h4 className="font-semibold text-yellow-800 mb-4 flex items-center">
                          <Target className="mr-2" size={18} />
                          Short-term Goals
                        </h4>
                        <div className="space-y-3">
                          {sampleAnalysis.recommendations.shortTerm.map((goal, index) => (
                            <div key={index} className="flex items-start space-x-2">
                              <Activity size={14} className="text-yellow-600 mt-1 flex-shrink-0" />
                              <span className="text-sm text-gray-700">{goal}</span>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Long-term Vision */}
                      <div className="bg-green-50 rounded-xl p-6">
                        <h4 className="font-semibold text-green-800 mb-4 flex items-center">
                          <Heart className="mr-2" size={18} />
                          Long-term Vision
                        </h4>
                        <div className="space-y-3">
                          {sampleAnalysis.recommendations.longTerm.map((vision, index) => (
                            <div key={index} className="flex items-start space-x-2">
                              <Star size={14} className="text-green-600 mt-1 flex-shrink-0" />
                              <span className="text-sm text-gray-700">{vision}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </Card>
          </motion.div>
        )}
      </AnimatePresence>

      {/* AI Insights */}
      {analysisComplete && (
        <Card className="bg-gradient-to-r from-accent-50 to-primary-50">
          <div className="flex items-start space-x-4">
            <div className="flex-shrink-0 w-12 h-12 bg-accent-100 rounded-xl flex items-center justify-center">
              <Brain className="w-6 h-6 text-accent-600" />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                AI Brand Strategy Insights
              </h3>
              <p className="text-gray-700 mb-4">
                Based on your personal brand analysis, here are strategic recommendations to strengthen your college applications:
              </p>
              <ul className="space-y-2">
                <li className="flex items-start space-x-2">
                  <ArrowRight size={16} className="text-accent-600 mt-0.5 flex-shrink-0" />
                  <span className="text-sm text-gray-700">
                    Your "Compassionate Innovator" brand differentiates you from typical STEM applicants
                  </span>
                </li>
                <li className="flex items-start space-x-2">
                  <ArrowRight size={16} className="text-accent-600 mt-0.5 flex-shrink-0" />
                  <span className="text-sm text-gray-700">
                    Focus on storytelling that connects your technical projects to social impact
                  </span>
                </li>
                <li className="flex items-start space-x-2">
                  <ArrowRight size={16} className="text-accent-600 mt-0.5 flex-shrink-0" />
                  <span className="text-sm text-gray-700">
                    Consider applying to programs that value both innovation and social responsibility
                  </span>
                </li>
              </ul>
            </div>
          </div>
        </Card>
      )}
    </div>
  );
};